/** Measurement helpers and copy shared by the FmSurfaceGallery parts. */
import { useEffect, useState } from 'react';
import { formatContrast } from './fmContrast';
import type { FmValueListItem } from './FmValueList';

export type RoleKey =
  | 'text'
  | 'secondary'
  | 'bandText'
  | 'bandMuted'
  | 'figureText'
  | 'figureMuted'
  | 'selected'
  | 'selectedRing'
  | 'untouched';

export interface FmSurfaceGalleryCopy {
  stageLabel: string;
  answerLabel: string;
  inspectorLabel: string;
  statesLabel: string;
  rolesTitle: string;
  renderedTitle: string;
  swatchesTitle: string;
  bandsTitle: string;
  untouchedTitle: string;
  untouchedLabel: string;
  measuring: string;
  textPass: string;
  graphicPass: string;
  fail: string;
  roles: Record<RoleKey, string>;
  tones: Record<'light' | 'paper' | 'deep', string>;
  grounds: Record<'paper' | 'imagery' | 'deep', string>;
  textRole: string;
  mutedRole: string;
}

export const TEXT_AA = 4.5;
export const GRAPHIC = 3;

export interface Measured {
  id: string;
  label: string;
  ratio: number;
  min: number;
}

export function verdict(copy: FmSurfaceGalleryCopy, row: Measured): string {
  if (row.ratio < row.min) return copy.fail;
  return row.min === GRAPHIC ? copy.graphicPass : copy.textPass;
}

export function valueItems(
  copy: FmSurfaceGalleryCopy,
  rows: readonly Measured[] | null,
  testId: string,
): FmValueListItem[] {
  if (rows === null)
    return [{ id: 'measuring', label: copy.measuring, value: '…', testId: `${testId}-measuring` }];
  return rows.map((row) => ({
    id: row.id,
    label: row.label,
    value: `${formatContrast(row.ratio)} · ${verdict(copy, row)}`,
    testId: `${testId}-${row.id}`,
  }));
}

/** Measures once the surface has laid out, and again after web fonts settle. */
export function useMeasured<T>(measure: () => T | null): T | null {
  const [value, setValue] = useState<T | null>(null);
  useEffect(() => {
    let cancelled = false;
    const run = () => {
      if (!cancelled) setValue(measure());
    };
    const frame = requestAnimationFrame(run);
    void document.fonts.ready.then(run);
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, [measure]);
  return value;
}
